/**
 * Shared color helpers. Pure functions — no React/RN imports — so any layer
 * (admin, resident, shared components) can derive a value from a live token.
 */

/** Neutral gray used when the input is not parseable as a 6-digit hex. */
const FALLBACK_RGB = '107, 114, 128';

/**
 * `#RRGGBB` (or a bare `RRGGBB`) -> `rgba(r, g, b, alpha)`.
 *
 * Use this whenever a fill, border or gradient stop needs an alpha variant of
 * a THEMED color: the same token resolves to a different hex in light and
 * dark, so hardcoding the rgba() would be right in one theme and wrong in the
 * other. It is also the correct way to write a "transparent" gradient stop —
 * the literal `'transparent'` is rgba(0,0,0,0) and interpolates through gray,
 * which smudges a light-surface fade.
 *
 * Anything that does not parse degrades to a neutral gray at the requested
 * alpha rather than throwing mid-render.
 */
export function hexToRgba(hex: string, alpha: number): string {
  const cleaned = hex.replace('#', '');
  const r = parseInt(cleaned.substring(0, 2), 16);
  const g = parseInt(cleaned.substring(2, 4), 16);
  const b = parseInt(cleaned.substring(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return `rgba(${FALLBACK_RGB}, ${alpha})`;
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
